import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import AuthGuard from '../components/AuthGuard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrophy, faMedal } from '@fortawesome/free-solid-svg-icons';

interface LeaderboardUser {
    id: number;
    name: string;
    phone: string;
    referral_code: string;
    downlines_count: number;
}

export default function Leaderboard() {
    const [users, setUsers] = useState<LeaderboardUser[]>([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLeaderboard = async () => {
            setLoading(true);
            try {
                const params = search ? { search } : {};
                const { data } = await api.get('/leaderboard', { params });
                setUsers(data?.data || []);
            } catch (error) {
                console.error('Failed to fetch leaderboard', error);
            } finally {
                setLoading(false);
            }
        };

        const timeoutId = setTimeout(() => {
            fetchLeaderboard();
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [search]);

    const getRankIcon = (index: number) => {
        if (index === 0) return <FontAwesomeIcon icon={faTrophy} className="text-yellow-400 text-xl" />;
        if (index === 1) return <FontAwesomeIcon icon={faMedal} className="text-gray-400 text-xl" />;
        if (index === 2) return <FontAwesomeIcon icon={faMedal} className="text-amber-700 text-xl" />;
        return <span className="text-gray-500 font-bold w-6 text-center">{index + 1}</span>;
    };

    return (
        <div className="max-w-md mx-auto min-h-[100vh] bg-gray-50 pb-20">
            <AuthGuard />

            {/* Header */}
            <div className="bg-[#e6212a] px-4 pt-12 pb-6 text-white text-center rounded-b-3xl shadow-md">
                <h1 className="text-2xl font-bold mb-2">邀请排行榜</h1>
                <p className="text-white/80 text-sm">查看大家的邀请战绩</p>
            </div>

            {/* Content */}
            <div className="px-4 -mt-4">
                {/* Search */}
                <div className="bg-white rounded-xl shadow-sm p-2 flex items-center gap-2 mb-4">
                    <FontAwesomeIcon icon={faSearch} className="text-gray-400 ml-2" />
                    <input
                        type="text"
                        placeholder="搜索用户名/手机号/推荐码"
                        className="flex-1 outline-none text-sm py-2"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                {/* List */}
                <div className="flex flex-col gap-3">
                    {loading ? (
                        <div className="text-center py-10 text-gray-400">加载中...</div>
                    ) : users.length > 0 ? (
                        users.map((user, index) => (
                            <div key={user.id} className="bg-white p-4 rounded-xl shadow-sm flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-8 flex justify-center">
                                        {getRankIcon(index)}
                                    </div>
                                    <div>
                                        <div className="font-bold text-gray-800">{user.name || '未命名'}</div>
                                        <div className="text-xs text-gray-400">ID: {user.referral_code || '-'}</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[#e6212a] font-bold text-lg">{user.downlines_count}</div>
                                    <div className="text-xs text-gray-400">邀请人数</div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-10 text-gray-400">暂无数据</div>
                    )}
                </div>
            </div>
        </div>
    );
}
